import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { authApi } from '../../api/auth';
import { useAuthStore } from '../../stores/auth';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const setSession = useAuthStore((state) => state.setSession);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useMutation({
    mutationFn: () => authApi.login(email, password),
    onSuccess: (session) => {
      setSession(session.user);
      navigate(location.state?.from ?? '/', { replace: true });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <h1 className="text-section-title font-semibold">{t('auth.login.title')}</h1>

      {login.isError ? <Alert tone="error">{errorText(login.error)}</Alert> : null}

      <Input
        label={t('auth.field.email')}
        name="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Input
        label={t('auth.field.password')}
        name="password"
        type="password"
        autoComplete="current-password"
        required
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />

      <Button type="submit" variant="primary" disabled={login.isPending}>
        {login.isPending ? t('auth.login.pending') : t('auth.login.submit')}
      </Button>

      <div className="flex items-center justify-between text-meta">
        <Link to="/forgot-password" className="text-content-secondary hover:text-content">{t('auth.login.forgot')}</Link>
        <Link to="/register" className="text-accent hover:text-accent-hover">{t('auth.register.title')}</Link>
      </div>
    </form>
  );
}
